import React, { useState } from 'react';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { useAuth } from '../../../context/AuthContext';
import { TaskCard } from '../../../components/tasks/TaskCard';
import { Button } from '../../../design-system/Button';
import { Badge } from '../../../design-system/Badge';
import { Input } from '../../../design-system/Input';
import { Select } from '../../../design-system/Dropdown';
import { Modal } from '../../../design-system/Modal';
import {
  Plus,
  Search,
  SlidersHorizontal,
  Layers,
  AlertTriangle
} from 'lucide-react';

export const BoardView = () => {
  const { user } = useAuth();
  const { tasks, moveTaskStatus, updateTask, setSelectedTask, addTask, setIsQuickAddOpen, members = [] } = useWorkspace();

  const [quickColumnAdd, setQuickColumnAdd] = useState(null); // column id
  const [quickTitle, setQuickTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [groupBy, setGroupBy] = useState('status'); // 'status', 'priority', 'type', 'assignee'
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [dragOverCol, setDragOverCol] = useState(null);

  // Column WIP limits & configurations
  const [columnWipLimits, setColumnWipLimits] = useState({
    backlog: 0,
    todo: 0,
    in_progress: 3,
    review: 2,
    done: 0
  });

  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false);
  const [editingWipLimits, setEditingWipLimits] = useState({ ...columnWipLimits });

  // Default Status Columns
  const statusColumns = [
    { id: 'backlog', title: 'Backlog', icon: 'inventory_2', color: 'neutral' },
    { id: 'todo', title: 'To Do', icon: 'radio_button_unchecked', color: 'neutral' },
    { id: 'in_progress', title: 'In Progress', icon: 'pending', color: 'teal' },
    { id: 'review', title: 'Review', icon: 'rate_review', color: 'purple' },
    { id: 'done', title: 'Done', icon: 'check_circle', color: 'success' }
  ];

  const priorityColumns = [
    { id: 'urgent', title: 'Urgent', icon: 'error', color: 'danger' },
    { id: 'high', title: 'High', icon: 'warning', color: 'warning' },
    { id: 'medium', title: 'Medium', icon: 'schedule', color: 'info' },
    { id: 'low', title: 'Low', icon: 'check_circle', color: 'neutral' }
  ];

  const typeColumns = [
    { id: 'feature', title: 'Features', icon: 'sparkles', color: 'teal' },
    { id: 'task', title: 'Tasks', icon: 'check_box', color: 'info' },
    { id: 'bug', title: 'Bugs', icon: 'pest_control', color: 'danger' },
    { id: 'chore', title: 'Chores', icon: 'layers', color: 'neutral' }
  ];

  const assigneeColumns = [
    ...members.map((m) => ({
      id: m.user.name,
      title: m.user.name,
      icon: 'person',
      avatar: m.user.avatar,
      color: 'teal'
    })),
    { id: 'unassigned', title: 'Unassigned', icon: 'person_off', color: 'neutral' }
  ];

  const activeColumns =
    groupBy === 'priority'
      ? priorityColumns
      : groupBy === 'type'
      ? typeColumns
      : groupBy === 'assignee'
      ? assigneeColumns
      : statusColumns;

  // Filter Tasks
  const filteredTasks = tasks.filter((t) => {
    const matchesSearch =
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPriority = priorityFilter === 'all' || t.priority === priorityFilter;
    return matchesSearch && matchesPriority;
  });

  const handleDragStart = (e, taskId) => {
    e.dataTransfer.setData('text/plain', taskId);
  };

  const handleDragOver = (e, colId) => {
    e.preventDefault();
    if (dragOverCol !== colId) {
      setDragOverCol(colId);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverCol(null);
  };

  const handleDrop = (e, targetColId) => {
    e.preventDefault();
    setDragOverCol(null);
    const taskId = e.dataTransfer.getData('text/plain');
    if (!taskId) return;

    if (groupBy === 'status') {
      moveTaskStatus(taskId, targetColId);
    } else if (groupBy === 'priority') {
      updateTask(taskId, { priority: targetColId });
    } else if (groupBy === 'type') {
      updateTask(taskId, { taskType: targetColId });
    } else if (groupBy === 'assignee') {
      if (targetColId === 'unassigned') {
        updateTask(taskId, { assignee: null });
      } else {
        const mem = members.find((m) => m.user.name === targetColId);
        updateTask(taskId, {
          assignee: mem ? { name: mem.user.name, avatar: mem.user.avatar } : { name: targetColId }
        });
      }
    }
  };

  const handleQuickAdd = (colId) => {
    if (!quickTitle.trim()) return;

    const currentUserName = user?.name || 'Workspace Owner';
    const currentUserAvatar = user?.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(currentUserName)}`;

    const newTaskData = {
      title: quickTitle.trim(),
      priority: 'medium',
      status: 'todo',
      assignee: { name: currentUserName, avatar: currentUserAvatar }
    };

    if (groupBy === 'status') newTaskData.status = colId;
    if (groupBy === 'priority') newTaskData.priority = colId;
    if (groupBy === 'type') newTaskData.taskType = colId;
    if (groupBy === 'assignee') {
      const mem = members.find((m) => m.user.name === colId);
      newTaskData.assignee = mem ? { name: mem.user.name, avatar: mem.user.avatar } : { name: colId };
    }

    addTask(newTaskData);
    setQuickTitle('');
    setQuickColumnAdd(null);
  };

  const handleSaveWipLimits = (e) => {
    e?.preventDefault();
    setColumnWipLimits(editingWipLimits);
    setIsColumnConfigOpen(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }} className="animate-fade-in">
      {/* Board Controls Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, maxWidth: '480px' }}>
          <Input
            placeholder="Search tasks on board..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<Search size={15} />}
          />

          <div style={{ width: '150px' }}>
            <Select
              options={[
                { value: 'status', label: 'Group: Status' },
                { value: 'priority', label: 'Group: Priority' },
                { value: 'type', label: 'Group: Type' },
                { value: 'assignee', label: 'Group: Assignee' }
              ]}
              value={groupBy}
              onChange={setGroupBy}
              size="sm"
            />
          </div>
        </div>

        {/* Right Toolbar Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Priority Quick Filter */}
          <div style={{ display: 'flex', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-sm)', padding: '2px' }}>
            {['all', 'urgent', 'high'].map((p) => (
              <button
                key={p}
                onClick={() => setPriorityFilter(p)}
                style={{
                  padding: '5px 10px',
                  borderRadius: 'var(--radius-sm)',
                  backgroundColor: priorityFilter === p ? 'var(--surface-card)' : 'transparent',
                  boxShadow: priorityFilter === p ? 'var(--shadow-raised-sm)' : 'none',
                  border: 'none',
                  color: priorityFilter === p ? 'var(--accent-teal)' : 'var(--text-muted)',
                  fontSize: '11px',
                  fontWeight: '700',
                  textTransform: 'capitalize',
                  cursor: 'pointer'
                }}
              >
                {p === 'all' ? 'All Priorities' : p}
              </button>
            ))}
          </div>

          <button
            onClick={() => {
              setEditingWipLimits({ ...columnWipLimits });
              setIsColumnConfigOpen(true);
            }}
            style={{
              padding: '7px 10px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--surface-recessed)',
              border: '1px solid var(--border-subtle)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              fontWeight: '600'
            }}
            title="Configure WIP limits & columns"
          >
            <SlidersHorizontal size={14} />
            <span>WIP Limits</span>
          </button>

          <Button
            variant="primary"
            size="sm"
            icon={<Plus size={15} />}
            onClick={() => setIsQuickAddOpen(true)}
          >
            Add Task
          </Button>
        </div>
      </div>

      {/* Kanban Board Columns Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${activeColumns.length}, minmax(280px, 1fr))`,
          gap: '16px',
          overflowX: 'auto',
          paddingBottom: '20px',
          alignItems: 'start'
        }}
      >
        {activeColumns.map((col) => {
          const colTasks = filteredTasks.filter((t) => {
            if (groupBy === 'status') return t.status === col.id;
            if (groupBy === 'priority') return t.priority === col.id;
            if (groupBy === 'type') return (t.taskType || 'task') === col.id;
            if (groupBy === 'assignee') {
              if (col.id === 'unassigned') return !t.assignee;
              const aName = typeof t.assignee === 'object' ? t.assignee?.name : t.assignee;
              return aName === col.id;
            }
            return false;
          });

          const isAdding = quickColumnAdd === col.id;
          const isOver = dragOverCol === col.id;

          const wipLimit = groupBy === 'status' ? columnWipLimits[col.id] || 0 : 0;
          const isWipExceeded = wipLimit > 0 && colTasks.length > wipLimit;

          return (
            <div
              key={col.id}
              onDragOver={(e) => handleDragOver(e, col.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, col.id)}
              style={{
                backgroundColor: isOver ? 'rgba(35, 190, 179, 0.08)' : 'var(--surface-recessed)',
                boxShadow: isOver ? '0 0 0 2px var(--accent-teal)' : 'var(--shadow-recessed-sm)',
                border: isOver ? '1px solid var(--accent-teal)' : isWipExceeded ? '1px solid var(--color-warning)' : '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-card)',
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                minHeight: '520px',
                transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {/* Column Header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 6px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {col.avatar ? (
                    <img
                      src={col.avatar}
                      alt={col.title}
                      style={{ width: '20px', height: '20px', borderRadius: '999px', objectFit: 'cover' }}
                    />
                  ) : (
                    <span
                      className="material-symbols-outlined"
                      style={{
                        fontSize: '18px',
                        color: col.color === 'teal' ? 'var(--accent-teal)' : col.color === 'danger' ? 'var(--color-danger)' : 'var(--text-muted)'
                      }}
                    >
                      {col.icon}
                    </span>
                  )}

                  <span style={{ fontSize: '14px', fontWeight: '800', color: 'var(--text-primary)' }}>
                    {col.title}
                  </span>

                  <Badge
                    size="xs"
                    variant={isWipExceeded ? 'warning' : col.color === 'teal' ? 'teal' : 'neutral'}
                  >
                    {wipLimit > 0 ? `${colTasks.length} / ${wipLimit}` : colTasks.length}
                  </Badge>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {isWipExceeded && (
                    <span title="WIP Limit Exceeded!" style={{ color: 'var(--color-warning)', display: 'flex', alignItems: 'center' }}>
                      <AlertTriangle size={15} />
                    </span>
                  )}
                  <button
                    onClick={() => setQuickColumnAdd(isAdding ? null : col.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: '4px'
                    }}
                    title="Quick add to column"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              {/* Inline Quick Add Field */}
              {isAdding && (
                <div
                  style={{
                    padding: '10px',
                    backgroundColor: 'var(--surface-card)',
                    boxShadow: 'var(--shadow-raised-sm)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-subtle)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}
                  className="animate-fade-in"
                >
                  <Input
                    placeholder="Task summary..."
                    value={quickTitle}
                    onChange={(e) => setQuickTitle(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleQuickAdd(col.id);
                      if (e.key === 'Escape') setQuickColumnAdd(null);
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                    <Button variant="ghost" size="xs" onClick={() => setQuickColumnAdd(null)}>
                      Cancel
                    </Button>
                    <Button variant="primary" size="xs" onClick={() => handleQuickAdd(col.id)}>
                      Add
                    </Button>
                  </div>
                </div>
              )}

              {/* Tasks List Container */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minHeight: '120px' }}>
                {colTasks.map((t) => (
                  <div
                    key={t.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, t.id)}
                    style={{ cursor: 'grab' }}
                  >
                    <TaskCard task={t} onClick={() => setSelectedTask(t)} />
                  </div>
                ))}

                {colTasks.length === 0 && !isAdding && (
                  <div
                    style={{
                      padding: '32px 16px',
                      textAlign: 'center',
                      color: 'var(--text-muted)',
                      fontSize: '12px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Layers size={22} style={{ opacity: 0.4 }} />
                    <span>No tasks in {col.title}</span>
                  </div>
                )}
              </div>

              {/* Bottom Quick Add Trigger */}
              {!isAdding && (
                <button
                  onClick={() => setQuickColumnAdd(col.id)}
                  style={{
                    padding: '8px',
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'transparent',
                    border: '1px dashed var(--border-subtle)',
                    color: 'var(--text-muted)',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    marginTop: 'auto',
                    transition: 'all 0.15s'
                  }}
                  className="hover-subtle"
                >
                  <Plus size={14} />
                  <span>Add Task</span>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* WIP Limits Configuration Modal */}
      <Modal
        isOpen={isColumnConfigOpen}
        onClose={() => setIsColumnConfigOpen(false)}
        title="Configure Board WIP Limits"
        subtitle="Set Work-In-Progress limits for each lifecycle stage to prevent development bottlenecks."
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsColumnConfigOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveWipLimits}>
              Save WIP Configuration
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {statusColumns.map((col) => (
            <div
              key={col.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                backgroundColor: 'var(--surface-recessed)',
                borderRadius: 'var(--radius-md)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', color: 'var(--accent-teal)' }}>
                  {col.icon}
                </span>
                <span style={{ fontSize: '14px', fontWeight: '700' }}>{col.title}</span>
              </div>

              <div style={{ width: '120px' }}>
                <Input
                  type="number"
                  min="0"
                  max="50"
                  value={editingWipLimits[col.id] || 0}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10) || 0;
                    setEditingWipLimits((prev) => ({ ...prev, [col.id]: val }));
                  }}
                  helperText={editingWipLimits[col.id] > 0 ? `Max ${editingWipLimits[col.id]} tasks` : 'No limit'}
                />
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};
